import AdminCourses from "@/v2/admin/Courses";
import AdminCatalog from "@/v2/admin/Catalog";
import { Navigate, NavLink, useParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/v2/lib/rbac";
import { GlassCard } from "@/v2/admin/ui";
import AdminAnalytics from "@/v2/admin/Analytics";
import AdminEmployees from "@/v2/admin/Employees";
import AdminJoinRequests from "@/v2/admin/JoinRequests";
import AdminDepartments from "@/v2/admin/Departments";
import AdminReports from "@/v2/admin/Reports";
import {
  UserPlus, Users, Building2, Target, Route, Swords, Clapperboard, Paperclip, BookOpen,
  FileQuestion, BarChart3, ClipboardList, Coins, FileText, KeyRound,
} from "lucide-react";

const PEOPLE = [
  { key: "requests", label: "طلبات الانضمام", icon: UserPlus, perm: "manage_users", component: AdminJoinRequests },
  { key: "employees", label: "الموظفون", icon: Users, perm: "view_users", component: AdminEmployees },
  { key: "departments", label: "الإدارات", icon: Building2, perm: "manage_users", component: AdminDepartments },
];

const GeneralCourses = () => <AdminCourses general />;

const CONTENT = [
  { key: "general", label: "دورات عامة", icon: BookOpen, perm: "manage_content", component: GeneralCourses },
  { key: "courses", label: "الدورات", icon: Clapperboard, perm: "manage_content", component: AdminCourses },
  { key: "catalog", label: "الكتالوج والأعداد", icon: ClipboardList, perm: "manage_content", component: AdminCatalog },
];

const PERFORMANCE = [
  { key: "analytics", label: "التحليلات", icon: BarChart3, perm: "view_reports", component: AdminAnalytics },
  { key: "reports", label: "التقارير", icon: FileText, perm: "view_reports", component: AdminReports },
];

function ControlHub({ base, title, description, tabs }) {
  const { user } = useAuth();
  const { section } = useParams();
  const visible = tabs.filter((tab) => can(user, tab.perm) && (!user.supervision || can(user, "view_admin") || tab.key !== "analytics"));
  if (!visible.length) return <Navigate to="/admin" replace />;
  if (!section) return <Navigate to={`/admin/${base}/${visible[0].key}`} replace />;

  const active = visible.find((tab) => tab.key === section);
  if (!active) return <Navigate to={`/admin/${base}/${visible[0].key}`} replace />;
  const ActivePage = active.component;

  return (
    <div className="space-y-5" data-testid={`admin-${base}-hub`}>
      <GlassCard className="p-4 sm:p-5">
        <div className="mb-4">
          <h1 className="text-lg sm:text-xl font-extrabold">{title}</h1>
          <p className="text-xs sm:text-sm text-ink-2 mt-1">{description}</p>
        </div>
        {visible.length > 1 && <nav className="flex gap-2 overflow-x-auto pb-1" aria-label={title}>
          {visible.map((tab) => (
            <NavLink
              key={tab.key}
              to={`/admin/${base}/${tab.key}`}
              data-testid={`${base}-tab-${tab.key}`}
              className={({ isActive }) =>
                `shrink-0 inline-flex items-center gap-2 h-10 px-4 rounded-xl text-xs sm:text-sm font-bold transition-all ${
                  isActive
                    ? "bg-ok/18 border border-ok/35 text-ink"
                    : "bg-surface-2 border border-line text-ink-2 hover:text-ink hover:bg-surface-2"
                }`
              }
            >
              <tab.icon size={15} /> {tab.label}
            </NavLink>
          ))}
        </nav>}
      </GlassCard>
      <ActivePage />
    </div>
  );
}

export const PeopleHub = () => {
  const { user } = useAuth();
  return <ControlHub
    base="people"
    title={user.supervision ? "موظفو نطاقي" : "إدارة الأفراد والهيكل التنظيمي"}
    description={user.supervision ? "اطلع على بيانات موظفي نطاقك وتفاصيل تدريبهم." : "اعتماد أصحاب المناصب وإدارة الحسابات وصلاحيات مسار وتميز من مساحة واحدة."}
    tabs={PEOPLE}
  />;
};

export const ContentHub = () => (
  <ControlHub
    base="content"
    title="إدارة الدورات"
    description="أضف الدورة واربطها بالجدارة، ثم رتّب دروسها."
    tabs={CONTENT}
  />
);

export const PerformanceHub = () => (
  <ControlHub
    base="performance"
    title="الأداء والقياس"
    description="تابع تقدّم الموظفين في الدورات."
    tabs={PERFORMANCE}
  />
);
